import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Stack, Paper, Chip, IconButton, Tooltip,
  CircularProgress, Divider, Select, MenuItem, FormControl, TextField,
  InputAdornment
} from '@mui/material';
import { ArrowBack, Refresh, Search, Security } from '@mui/icons-material';
import { apiRequest } from '../../utils/api';

const eventLabels = {
  login_success: { label: 'Login OK', color: '#10B981' },
  login_failed: { label: 'Login fallido', color: '#F59E0B' },
  account_locked: { label: 'Cuenta bloqueada', color: '#EF4444' },
  password_changed: { label: 'Cambio contraseña', color: '#2563EB' },
  password_reset: { label: 'Reset contraseña', color: '#7C3AED' },
  session_closed: { label: 'Sesión cerrada', color: '#475569' },
};

export default function SecurityLogs() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const fetchEvents = useCallback(async () => {
    try {
      let url = `/admin/security-events?page=${page}`;
      if (typeFilter !== 'all') url += `&type=${typeFilter}`;
      const data = await apiRequest(url);
      setEvents(data.events || []);
    } catch { setEvents([]); }
    setLoading(false);
  }, [page, typeFilter]);

  useEffect(() => { setLoading(true); fetchEvents(); }, [fetchEvents]);

  const filtered = events.filter(e =>
    search === '' || (e.user_email || '').toLowerCase().includes(search.toLowerCase())
      || (e.ip_address || '').includes(search)
  );

  const formatTime = (t) => new Date(t).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 30 }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/admin/control-room')} size="small"><ArrowBack /></IconButton>
              <Typography variant="h5" sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #2563EB, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Vincula</Typography>
              <Chip label="Logs de Seguridad" size="small" variant="outlined" icon={<Security sx={{ fontSize: 14 }} />} />
            </Stack>
            <Tooltip title="Refrescar"><IconButton onClick={fetchEvents}><Refresh /></IconButton></Tooltip>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField size="small" placeholder="Buscar por email o IP..." value={search}
              onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94A3B8' }} /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">Todos los eventos</MenuItem>
                <MenuItem value="login_failed">Login fallido</MenuItem>
                <MenuItem value="login_success">Login exitoso</MenuItem>
                <MenuItem value="account_locked">Cuenta bloqueada</MenuItem>
                <MenuItem value="password_changed">Cambio contraseña</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No hay eventos</Typography></Box>
          ) : (
            filtered.map((e, idx) => {
              const config = eventLabels[e.event_type] || { label: e.event_type, color: '#475569' };
              return (
                <React.Fragment key={e.id}>
                  {idx > 0 && <Divider />}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Chip label={config.label} size="small" sx={{ bgcolor: `${config.color}15`, color: config.color, fontWeight: 600, fontSize: 11, minWidth: 120 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{e.user_email || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(e.created_at)} · IP: {e.ip_address || '—'}
                        </Typography>
                      </Box>
                    </Stack>
                    {e.details && e.details !== '{}' && (
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {e.details}
                      </Typography>
                    )}
                  </Stack>
                </React.Fragment>
              );
            })
          )}
        </Paper>

        {events.length >= 50 && (
          <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
            {page > 0 && <Chip label="← Anterior" onClick={() => setPage(p => p - 1)} clickable />}
            <Chip label="Siguiente →" onClick={() => setPage(p => p + 1)} clickable />
          </Stack>
        )}
      </Container>
    </Box>
  );
}
